import React, { useState, useEffect } from 'react';
import { Bell, CheckCircle, AlertCircle, Info, AlertTriangle, Calendar, Banknote, Car, Star, UserX } from 'lucide-react';

interface NotificationPreference {
  type: string;
  label: string;
  description: string;
  enabled: boolean;
  icon: React.ReactNode;
  color: string;
}

const AdminNotificationSettingsPage: React.FC = () => {
  const [preferences, setPreferences] = useState<NotificationPreference[]>([
    {
      type: 'booking_created',
      label: 'New Bookings',
      description: 'Get notified when a new booking is created',
      enabled: true,
      icon: <Calendar className="h-5 w-5" />,
      color: 'blue'
    },
    {
      type: 'booking_confirmed',
      label: 'Booking Confirmations',
      description: 'Notifications when bookings are confirmed',
      enabled: true,
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'green'
    },
    {
      type: 'booking_cancelled',
      label: 'Booking Cancellations',
      description: 'Alerts when bookings are cancelled',
      enabled: true,
      icon: <AlertCircle className="h-5 w-5" />,
      color: 'red'
    },
    {
      type: 'booking_completed',
      label: 'Completed Bookings',
      description: 'Notifications when services are completed',
      enabled: true,
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'green'
    },
    {
      type: 'payment_received',
      label: 'Payment Notifications',
      description: 'Alerts for successful payments',
      enabled: true,
      icon: <Banknote className="h-5 w-5" />,
      color: 'emerald'
    },
    {
      type: 'driver_request',
      label: 'Driver Requests',
      description: 'New driver booking requests awaiting assignment',
      enabled: true,
      icon: <Car className="h-5 w-5" />,
      color: 'black'
    },
    {
      type: 'booking_reminder',
      label: 'Booking Reminders',
      description: 'Reminders for upcoming bookings',
      enabled: true,
      icon: <Bell className="h-5 w-5" />,
      color: 'purple'
    },
    {
      type: 'rating',
      label: 'Rating Notifications',
      description: 'When drivers receive new ratings',
      enabled: false,
      icon: <Star className="h-5 w-5" />,
      color: 'yellow'
    },
    {
      type: 'error',
      label: 'Error Alerts',
      description: 'Critical system errors and urgent complaints',
      enabled: true,
      icon: <AlertTriangle className="h-5 w-5" />,
      color: 'red'
    },
    {
      type: 'success',
      label: 'Success Messages',
      description: 'Positive confirmations and completions',
      enabled: true,
      icon: <CheckCircle className="h-5 w-5" />,
      color: 'green'
    },
    {
      type: 'info',
      label: 'Informational',
      description: 'General information and updates',
      enabled: true,
      icon: <Info className="h-5 w-5" />,
      color: 'blue'
    },
    {
      type: 'account_deletion',
      label: 'Account Deletion Requests',
      description: 'Notifications when users request account deletion',
      enabled: true,
      icon: <UserX className="h-5 w-5" />,
      color: 'red'
    }
  ]);

  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  useEffect(() => {
    // Load saved preferences from localStorage
    const savedPreferences = localStorage.getItem('adminNotificationPreferences');
    if (savedPreferences) {
      try {
        const parsed = JSON.parse(savedPreferences);
        setPreferences(prevPrefs =>
          prevPrefs.map(pref => ({
            ...pref,
            enabled: parsed[pref.type] !== undefined ? parsed[pref.type] : pref.enabled
          }))
        );
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    }
  }, []);

  const handleToggle = (type: string) => {
    setPreferences(prevPrefs =>
      prevPrefs.map(pref =>
        pref.type === type ? { ...pref, enabled: !pref.enabled } : pref
      )
    );
  };

  const handleSave = () => {
    setSaving(true);
    
    // Convert preferences array to object for easier storage and retrieval
    const preferencesObject = preferences.reduce((acc, pref) => {
      acc[pref.type] = pref.enabled;
      return acc;
    }, {} as Record<string, boolean>);

    // Save to localStorage
    localStorage.setItem('adminNotificationPreferences', JSON.stringify(preferencesObject));
    
    // Simulate API call
    setTimeout(() => {
      setSaving(false);
      setSaveSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    }, 500);
  };

  const enabledCount = preferences.filter(p => p.enabled).length;

  const getColorClasses = (color: string, enabled: boolean) => {
    if (!enabled) return 'bg-gray-100 text-gray-400 border-gray-200';
    
    const colorMap: Record<string, string> = {
      blue: 'bg-blue-100 text-blue-600 border-blue-300',
      green: 'bg-green-100 text-green-600 border-green-300',
      red: 'bg-red-100 text-red-600 border-red-300',
      emerald: 'bg-emerald-100 text-emerald-600 border-emerald-300',
      black: 'bg-gray-100 text-black border-gray-300',
      purple: 'bg-purple-100 text-purple-600 border-purple-300',
      yellow: 'bg-yellow-100 text-yellow-600 border-yellow-300'
    };
    
    return colorMap[color] || 'bg-white text-black border-gray-300';
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white">Notification Settings</h1>
          <p className="text-gray-300 mt-2">
            Manage which types of notifications you want to receive. Changes are saved automatically.
          </p>
        </div>

        {/* Summary Card */}
        <div className="bg-gradient-to-r from-black to-gray-600 rounded-lg shadow-lg p-6 mb-8 text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Active Notifications</h2>
              <p className="text-white">
                {enabledCount} of {preferences.length} notification types enabled
              </p>
            </div>
            <div className="bg-white bg-opacity-20 rounded-full p-4">
              <Bell className="h-12 w-12" />
            </div>
          </div>
        </div>

        {/* Success Message */}
        {saveSuccess && (
          <div className="mb-6 bg-green-50 border-2 border-green-500 rounded-lg p-4 flex items-center animate-fade-in">
            <CheckCircle className="h-5 w-5 text-green-600 mr-3" />
            <p className="text-green-800 font-medium">Notification preferences saved successfully!</p>
          </div>
        )}

        {/* Notification Preferences */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="divide-y divide-gray-200">
            {preferences.map((pref, index) => (
              <div
                key={pref.type}
                className={`p-6 transition-colors ${
                  pref.enabled ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-start space-x-4 flex-1">
                    <div className={`p-3 rounded-lg border-2 ${getColorClasses(pref.color, pref.enabled)}`}>
                      {pref.icon}
                    </div>
                    <div className="flex-1">
                      <h3 className={`text-lg font-semibold mb-1 ${
                        pref.enabled ? 'text-gray-900' : 'text-gray-500'
                      }`}>
                        {pref.label}
                      </h3>
                      <p className={`text-sm ${
                        pref.enabled ? 'text-gray-600' : 'text-gray-400'
                      }`}>
                        {pref.description}
                      </p>
                    </div>
                  </div>
                  
                  {/* Toggle Switch */}
                  <button
                    onClick={() => handleToggle(pref.type)}
                    className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 ${
                      pref.enabled ? 'bg-black' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                        pref.enabled ? 'translate-x-7' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mt-8 flex gap-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex-1 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                Saving...
              </>
            ) : (
              <>
                <CheckCircle className="h-5 w-5 mr-2" />
                Save Preferences
              </>
            )}
          </button>
          
          <button
            onClick={() => {
              setPreferences(prevPrefs =>
                prevPrefs.map(pref => ({ ...pref, enabled: true }))
              );
            }}
            className="px-6 py-3 border-2 border-black text-black rounded-lg hover:bg-white transition-colors font-medium"
          >
            Enable All
          </button>
        </div>

        {/* Info Box */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" />
            <div className="text-sm text-blue-700">
              <p className="font-medium mb-2">About Notification Settings:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>These settings control which notification types appear in your notification bell</li>
                <li>Disabled notifications will still be logged but won't appear in your feed</li>
                <li>Critical error notifications and urgent complaints cannot be fully disabled for safety reasons</li>
                <li>Changes take effect immediately and persist across sessions</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminNotificationSettingsPage;

